import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  DollarSign, 
  Search, 
  Plus, 
  RefreshCw,
  Wrench,
  Clock,
  Ruler,
  Edit,
  FileText,
  Briefcase,
  BarChart3,
  MoreHorizontal
} from 'lucide-react';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import PHSCommandLayout from '@/components/phs/enterprise/PHSCommandLayout';

const API = process.env.REACT_APP_BACKEND_URL;

// Trade colors
const TRADE_COLORS = {
  'General': 'bg-slate-100 text-slate-700',
  'Electrical': 'bg-yellow-100 text-yellow-800',
  'Gas': 'bg-orange-100 text-orange-800',
  'Plumbing': 'bg-blue-100 text-blue-800',
  'Drainage': 'bg-purple-100 text-purple-800',
  'Plant': 'bg-green-100 text-green-800',
  'Materials': 'bg-amber-100 text-amber-800',
  'Roofing': 'bg-red-100 text-red-800',
  'Joinery': 'bg-teal-100 text-teal-800',
  'Groundworks': 'bg-cyan-100 text-cyan-800'
};

function RateLibraryPage() {
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tradeFilter, setTradeFilter] = useState('all');
  const [trades, setTrades] = useState([]);
  const [editingRate, setEditingRate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    loadRates();
  }, [tradeFilter, searchTerm]);
  
  const loadRates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tradeFilter && tradeFilter !== 'all') params.append('trade', tradeFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await axios.get(`${API}/api/phs/rates?${params}`, { headers });
      setRates(res.data.rates || []);
      setTrades(res.data.trades || []);
    } catch (err) {
      console.error('Failed to load rates:', err);
      toast.error('Failed to load rates');
    } finally {
      setLoading(false);
    }
  };
  
  const seedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/api/phs/rates/seed-defaults`, {}, { headers });
      toast.success(res.data.message);
      loadRates();
    } catch (err) {
      console.error('Failed to seed rates:', err);
      toast.error('Failed to seed default rates');
    }
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(value);
  };
  
  // Group rates by trade
  const groupedRates = rates.reduce((acc, rate) => {
    const trade = rate.trade || 'Other';
    if (!acc[trade]) acc[trade] = [];
    acc[trade].push(rate);
    return acc;
  }, {});
  
  return (
    <PHSCommandLayout>
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-teal-600" />
              Rate Library
            </h1>
            <p className="text-sm text-slate-500 mt-1">Internal schedule of rates by trade and material</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadRates}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={seedDefaults}>
              Seed Defaults
            </Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
              <Plus className="h-4 w-4 mr-1" />
              Add Rate
            </Button>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search rates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="rate-search"
            />
          </div>
          <Select value={tradeFilter} onValueChange={setTradeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Trades" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Trades</SelectItem>
              {trades.map(trade => (
                <SelectItem key={trade} value={trade}>{trade}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Trade Summary Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {trades.map(trade => {
          const count = groupedRates[trade]?.length || 0;
          const colorClass = TRADE_COLORS[trade] || 'bg-slate-100 text-slate-700';
          return (
            <button
              key={trade}
              onClick={() => setTradeFilter(trade === tradeFilter ? 'all' : trade)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${colorClass} ${
                trade === tradeFilter ? 'ring-2 ring-teal-500 ring-offset-2' : ''
              }`}
            >
              {trade} ({count})
            </button>
          );
        })}
      </div>
      
      {/* Rates Table */}
      {loading ? (
        <Card>
          <CardContent className="p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="animate-pulse flex items-center gap-4 py-3 border-b last:border-0">
                <div className="w-20 h-5 bg-slate-200 rounded" />
                <div className="flex-1 h-5 bg-slate-200 rounded" />
                <div className="w-16 h-5 bg-slate-200 rounded" />
                <div className="w-16 h-5 bg-slate-200 rounded" />
                <div className="w-16 h-5 bg-slate-200 rounded" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : rates.length === 0 ? (
        <Card className="p-8 text-center">
          <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Rates Found</h3>
          <p className="text-sm text-slate-500 mb-4">
            Click "Seed Defaults" to add standard rate data
          </p>
          <Button onClick={seedDefaults}>Seed Default Rates</Button>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <div className="col-span-2">Trade</div>
              <div className="col-span-3">Item</div>
              <div className="col-span-1">Unit</div>
              <div className="col-span-1 text-right">Low</div>
              <div className="col-span-1 text-right">Avg</div>
              <div className="col-span-1 text-right">High</div>
              <div className="col-span-1">Notes</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            
            {/* Table Body */}
            <div className="divide-y divide-slate-100">
              {rates.map(rate => {
                const colorClass = TRADE_COLORS[rate.trade] || 'bg-slate-100 text-slate-700';
                return (
                  <div 
                    key={rate.rate_id} 
                    className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-slate-50/50 transition items-center"
                    data-testid={`rate-row-${rate.rate_id}`}
                  >
                    <div className="col-span-2">
                      <Badge className={`text-[10px] ${colorClass}`}>{rate.trade}</Badge>
                    </div>
                    <div className="col-span-3">
                      <span className="text-sm font-medium text-slate-800">{rate.item_name}</span>
                    </div>
                    <div className="col-span-1">
                      <span className="text-xs text-slate-500">{rate.unit}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <span className="text-sm text-green-600">{formatCurrency(rate.low_rate)}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <span className="text-sm font-semibold text-slate-800">{formatCurrency(rate.average_rate)}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <span className="text-sm text-amber-600">{formatCurrency(rate.high_rate)}</span>
                    </div>
                    <div className="col-span-1">
                      <span className="text-xs text-slate-400 truncate">{rate.notes || '-'}</span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEditingRate(rate);
                            setShowEditModal(true);
                          }}
                          title="Edit Rate"
                          data-testid={`rate-edit-${rate.rate_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            navigate(`/phs/helpdesk?action=new-job&prefill_rate=${rate.rate_id}&prefill_type=${encodeURIComponent(rate.item_name)}`);
                          }}
                          title="Raise Job"
                          className="text-teal-600"
                          data-testid={`rate-raise-job-${rate.rate_id}`}
                        >
                          <Briefcase className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            toast.info(`Create quote with rate: ${rate.item_name}`);
                          }}
                          title="Create Quote"
                          className="text-purple-600"
                          data-testid={`rate-create-quote-${rate.rate_id}`}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            navigate(`/phs/benchmarking?add_rate=${rate.rate_id}`);
                          }}
                          title="Add to Benchmark"
                          className="text-blue-600"
                          data-testid={`rate-add-benchmark-${rate.rate_id}`}
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Edit Rate Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Rate</DialogTitle>
          </DialogHeader>
          {editingRate && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Item Name</label>
                <Input 
                  value={editingRate.item_name || ''} 
                  onChange={(e) => setEditingRate({...editingRate, item_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Low Rate</label>
                  <Input 
                    type="number" 
                    value={editingRate.low_rate || ''} 
                    onChange={(e) => setEditingRate({...editingRate, low_rate: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Average Rate</label>
                  <Input 
                    type="number" 
                    value={editingRate.average_rate || ''} 
                    onChange={(e) => setEditingRate({...editingRate, average_rate: parseFloat(e.target.value)})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">High Rate</label>
                  <Input 
                    type="number" 
                    value={editingRate.high_rate || ''} 
                    onChange={(e) => setEditingRate({...editingRate, high_rate: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Unit</label>
                <Input 
                  value={editingRate.unit || ''} 
                  onChange={(e) => setEditingRate({...editingRate, unit: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notes</label>
                <Textarea 
                  value={editingRate.notes || ''} 
                  onChange={(e) => setEditingRate({...editingRate, notes: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  await axios.put(`${API}/api/phs/rates/${editingRate.rate_id}`, editingRate, { headers });
                  toast.success('Rate updated successfully');
                  setShowEditModal(false);
                  loadRates();
                } catch (err) {
                  toast.error('Failed to update rate');
                }
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(RateLibraryPage, 'helpdesk');
